import { useTheme } from "@mui/material";
import { PDFTable } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables";
import { PDFTableBody } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-body";
import { PDFTableCell } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-cell";
import {
  DEFAULT_GAP_3,
  TITLE_HEIGHT,
} from "@pages/platform-analysis/components/buttons/export-pdf/constant";
import { useStyles } from "@pages/platform-analysis/components/buttons/export-pdf/styles";
import { generateFeatureSectionIconUrl } from "@pages/platform-analysis/components/buttons/export-pdf/util";
import { PDFReviewTableRow } from "@pages/reviews/components/buttons/export-pdf/layouts/tables/table-row";
import { Image, Text, View } from "@react-pdf/renderer";
import { isEmpty, isEqual, isNil } from "lodash";
import { PDFTableRow, PDFTableRowType } from "../../layouts/tables/table-row";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import {
  MAAnalysisDataDTO,
  SelectedSubProductDTO,
} from "@models/managed-accounts/entities/step/fee";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import {
  FeeAnalysisRowType,
  FeeAnalysisTableRow,
} from "@models/managed-accounts/entities/analysis";
import { RenderNumericFeesValue } from "@pages/managed-accounts/_id/steps/fee-analysis/components/tables/table-cell";
import { ManagedAccountFieldTypeValue } from "@models/managed-accounts/enums/fieldTypeValue";
import { ManagedAccountFieldType } from "@models/managed-accounts/enums/fieldType";
import { PDFPortfolioFeeTableConfigs as Configs } from "./configs";
import { PDFTitleSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/title-section";
import { PDFCheckSvg } from "@pages/platform-analysis/components/buttons/export-pdf/svgs/CheckSvg";
import { PDFCrossSvg } from "@pages/platform-analysis/components/buttons/export-pdf/svgs/CrossSvg";

interface PDFPortfolioFeeTableProps {
  data?: FeeAnalysisTableRow[];
  selectedSubProducts: SelectedSubProductDTO[];
  isAppending?: boolean;
  tableWidth?: boolean;
  showSubSection?: boolean;
  tableViewMode?: FeesDisplayStyle;
  tablePerformanceScope?: FeesPerformanceScope;
  isLastTable?: boolean;
  isFollowingTable?: boolean;
  countItemTitle?: string;
  numOfHiddenProducts?: number;
}

export const PDFPortfolioFeeTable = ({
  data,
  selectedSubProducts,
  isAppending = false,
  tableViewMode,
  tablePerformanceScope,
  isFollowingTable,
  countItemTitle,
  numOfHiddenProducts,
}: PDFPortfolioFeeTableProps) => {
  const { typographyStyle } = useStyles();
  const { palette } = useTheme();
  const shouldExcludePerformance =
    tablePerformanceScope === FeesPerformanceScope.Excluding;
  let shouldShowRedStarInformation = false;

  const renderImage = (
    isSubSectionType: boolean,
    section: FeeAnalysisTableRow,
  ) => {
    if (!isSubSectionType && !isNil(section?.iconUrl)) {
      const src = generateFeatureSectionIconUrl(section?.iconUrl);
      return <Image style={{ width: 10, height: 10 }} src={src} />;
    }
  };

  const renderRedStarIcon = (analysisData: MAAnalysisDataDTO) => {
    if (!analysisData.tooltip) return;

    const { header, data, additionalText } = analysisData.tooltip;

    if (header || data || additionalText) {
      shouldShowRedStarInformation = true;

      return (
        <Text
          style={{
            ...typographyStyle.body2,
            color: palette.error.main,
            paddingTop: 2,
            fontSize: Configs.DefaultFontSize,
          }}
        >
          *
        </Text>
      );
    }
  };

  const renderAnalysisDataValue = (
    variableName: string,
    analysisData: MAAnalysisDataDTO,
    fieldTypeId?: string,
  ) => {
    if (analysisData?.value === ManagedAccountFieldTypeValue.NotDisclosed) {
      const isCashInvestmentFee = isEqual(variableName, "CM_fee");

      return (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              textAlign: "center",
              fontSize: Configs.DefaultFontSize,
            }}
          >
            {analysisData.value}
          </Text>

          {isCashInvestmentFee && renderRedStarIcon(analysisData)}
        </View>
      );
    }

    switch (fieldTypeId) {
      case ManagedAccountFieldType.Currency:
        const isMultipleWithTooltip = [
          analysisData?.isMultiple,
          !isEmpty(analysisData?.tooltip),
          isEqual(analysisData?.value, ManagedAccountFieldType.Multiple),
        ].every(Boolean);

        return (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Text
              style={{
                ...typographyStyle.body2,
                color: palette.text.primary,
                fontSize: Configs.DefaultFontSize,
              }}
            >
              {isMultipleWithTooltip ? (
                <>Multiple</>
              ) : (
                <RenderNumericFeesValue
                  alwaysShowDecimal
                  value={analysisData?.value}
                  valuePercentage={analysisData?.valueInPercent}
                  tableViewMode={tableViewMode}
                />
              )}
            </Text>

            {renderRedStarIcon(analysisData) && <></>}
          </View>
        );
      case ManagedAccountFieldType.YesNo:
        switch (analysisData?.value) {
          case ManagedAccountFieldTypeValue.True:
            if (variableName === "Model_inception") {
              return (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      color: palette.text.primary,
                      fontSize: Configs.DefaultFontSize,
                    }}
                  >
                    Yes
                  </Text>
                  {renderRedStarIcon(analysisData)}
                </View>
              );
            }
            return <PDFCheckSvg />;
          case ManagedAccountFieldTypeValue.False:
            if (variableName === "Model_inception") {
              return (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Text
                    style={{
                      ...typographyStyle.body2,
                      color: palette.text.primary,
                      fontSize: Configs.DefaultFontSize,
                    }}
                  >
                    No
                  </Text>
                  <PDFCheckSvg />
                </View>
              );
            }
            return <PDFCrossSvg />;
          case ManagedAccountFieldType.Multiple:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: Configs.DefaultFontSize,
                  }}
                >
                  Multiple
                </Text>
                {renderRedStarIcon(analysisData)}
              </View>
            );
          default:
            return (
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: Configs.DefaultFontSize,
                }}
              >
                {analysisData?.value}
              </Text>
            );
        }
      case ManagedAccountFieldType.YesNoText:
        switch (analysisData?.value) {
          case ManagedAccountFieldTypeValue.True:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <PDFCheckSvg />
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: Configs.DefaultFontSize,
                  }}
                >
                  Net
                </Text>
              </View>
            );
          case ManagedAccountFieldTypeValue.False:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <PDFCrossSvg />
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: Configs.DefaultFontSize,
                  }}
                >
                  Gross
                </Text>
              </View>
            );
          case ManagedAccountFieldType.Multiple:
            return (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontSize: Configs.DefaultFontSize,
                  }}
                >
                  Multiple
                </Text>
                {renderRedStarIcon(analysisData)}
              </View>
            );
          default:
            return (
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: Configs.DefaultFontSize,
                }}
              >
                {analysisData?.value}
              </Text>
            );
        }
      case ManagedAccountFieldType.DateTime:
        if (analysisData?.value === ManagedAccountFieldTypeValue.Multiple) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: Configs.DefaultFontSize,
                }}
              >
                Multiple
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }
        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: Configs.DefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
      case ManagedAccountFieldType.Percentage:
        if (analysisData?.isMultiple && analysisData.tooltip) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: Configs.DefaultFontSize,
                }}
              >
                {analysisData.value === ManagedAccountFieldTypeValue.Multiple
                  ? ManagedAccountFieldTypeValue.Multiple
                  : `${analysisData?.value}%`}
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }

        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: Configs.DefaultFontSize,
            }}
          >
            <RenderNumericFeesValue
              valuePercentage={analysisData?.value}
              tableViewMode={FeesDisplayStyle.Percentage}
            />
          </Text>
        );
      case ManagedAccountFieldType.Warning:
        if (analysisData?.value === ManagedAccountFieldTypeValue.NoBreach) {
          return <PDFCheckSvg />;
        }

        if (analysisData?.value === "-") {
          return (
            <Text
              style={{
                ...typographyStyle.body2,
                color: palette.text.primary,
                fontSize: Configs.DefaultFontSize,
              }}
            >
              {analysisData?.value}
            </Text>
          );
        }

        return (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Text
              style={{
                ...typographyStyle.body2,
                color: palette.text.primary,
                fontSize: Configs.DefaultFontSize,
              }}
            >
              Breach
            </Text>
            {renderRedStarIcon(analysisData)}
          </View>
        );
      case ManagedAccountFieldType.Dropdown:
        if (analysisData?.value === ManagedAccountFieldTypeValue.Multiple) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: Configs.DefaultFontSize,
                }}
              >
                {analysisData?.value}
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }
        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: Configs.DefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
      case ManagedAccountFieldType.Text:
        if (analysisData?.isMultiple && analysisData.tooltip) {
          return (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.text.primary,
                  fontSize: Configs.DefaultFontSize,
                }}
              >
                Multiple
              </Text>
              {renderRedStarIcon(analysisData)}
            </View>
          );
        }

        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: Configs.DefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
      default:
        return (
          <Text
            style={{
              ...typographyStyle.body2,
              color: palette.text.primary,
              fontSize: Configs.DefaultFontSize,
            }}
          >
            {analysisData?.value}
          </Text>
        );
    }
  };

  const getTableTitle = () => {
    return isFollowingTable
      ? "SMA portfolio fees by platform (continued)"
      : "SMA portfolio fees by platform";
  };

  const renderTableHeader = () => {
    const tableTitle = getTableTitle();

    if (isAppending) {
      return (
        <View style={{ height: TITLE_HEIGHT }}>
          <Text
            style={{
              ...typographyStyle.subtitle,
              color: palette.common.black,
            }}
          >
            {tableTitle}
          </Text>
        </View>
      );
    }

    return (
      <>
        <PDFTitleSection
          title="Fee analysis (continued)"
          subTitle={tableTitle}
        />
        {selectedSubProducts?.length > 0 && (
          <View style={{ height: 20, marginTop: -1 }}>
            <Text
              style={{
                ...typographyStyle.body1,
                color: palette.common.black,
              }}
            >
              {countItemTitle}
            </Text>
          </View>
        )}
      </>
    );
  };

  const calculateLinesForText = (text: string | undefined) => {
    if (!text?.trim()) return 0;

    const words = text.trim().split(" ");
    const spaceLen = 1;
    let lines = 1;
    let currentLineLen = 0;

    for (const word of words) {
      const wordLen = word.length;
      if (wordLen <= Configs.DefaultMaxCharsPerLineOfValue) {
        if (currentLineLen === 0) {
          currentLineLen = wordLen;
          continue;
        }

        const appendedNextWordLen = currentLineLen + spaceLen + wordLen;

        if (appendedNextWordLen <= Configs.DefaultMaxCharsPerLineOfValue) {
          currentLineLen = appendedNextWordLen;
          continue;
        }

        lines += 1;
        currentLineLen = wordLen;
      } else {
        lines += Math.floor(wordLen / Configs.DefaultMaxCharsPerLineOfValue);
        const remainder = wordLen % Configs.DefaultMaxCharsPerLineOfValue;

        currentLineLen = remainder > 0 ? remainder : 0;
      }
    }

    return lines;
  };

  const getTableHeaderRowType = (): PDFTableRowType => {
    const numOfLines = selectedSubProducts.map(
      (sp) =>
        calculateLinesForText(sp.productName) + calculateLinesForText(sp.name),
    );

    const maxLines = Math.max(...numOfLines, 0);

    const lineTypeMap: Record<number, PDFTableRowType> = {
      3: "HeaderTripleLineNoLogo",
      4: "HeaderQuadrupleLineNoLogo",
      5: "HeaderQuintupleLineNoLogo",
      6: "HeaderSextupleLineNoLogo",
    };

    return lineTypeMap[maxLines] ?? "HeaderDoubleLineNoLogo";
  };

  const breakLongWord = (word: string | undefined) => {
    if (!word) return "";

    return word
      .split(" ")
      .map((word) =>
        word.length > Configs.DefaultMaxCharsPerLineOfValue
          ? word.replace(
              new RegExp(`(.{${Configs.DefaultMaxCharsPerLineOfValue}})`, "g"),
              "$1 ",
            )
          : word,
      )
      .join(" ");
  };

  return (
    <>
      {renderTableHeader()}

      {selectedSubProducts?.length === 0 && (
        <View style={{ gap: "5px", marginTop: 15, marginBottom: 15 }}>
          <Text
            style={{
              ...typographyStyle.body1,
              fontWeight: "bold",
              color: "#b1b6bf",
            }}
          >
            No data
          </Text>
          <Text style={{ ...typographyStyle.body1, color: "#b1b6bf" }}>
            No products match the criteria in your filter
          </Text>
        </View>
      )}

      {selectedSubProducts?.length > 0 && (
        <>
          <PDFTable
            tableWidth={
              Configs.FirstCellWidth +
              selectedSubProducts?.length * Configs.FollowingCellWidth
            }
          >
            {/* Header */}
            <PDFTableRow type={getTableHeaderRowType()} forceShowBorderBottom>
              <PDFTableCell width={Configs.FirstCellWidth}>
                <Text
                  style={{
                    ...typographyStyle.body2,
                    color: palette.text.primary,
                    fontWeight: "bold",
                    textAlign: "center",
                  }}
                >
                  Total fees and costs{" "}
                  {tablePerformanceScope === FeesPerformanceScope.Including
                    ? "including"
                    : "excluding"}
                  {"\n"}
                  performance fees (p.a.)
                </Text>
              </PDFTableCell>

              {selectedSubProducts?.map((subProduct, index) => (
                <PDFTableCell width={Configs.FollowingCellWidth} key={index}>
                  {subProduct.isProductMain && (
                    <Text
                      style={{
                        ...typographyStyle.body2,
                        alignItems: "center",
                        borderRadius: 10,
                        borderWidth: 0.5,
                        borderColor: "#FFAC8A",
                        backgroundColor: "#FFE3D8",
                        fontWeight: "bold",
                        color: "#E84D1E",
                        position: "absolute",
                        top: 1,
                        right: 1,
                        fontSize: 5,
                        paddingTop: 2,
                        paddingRight: 1,
                        paddingLeft: 2,
                      }}
                    >
                      Main
                    </Text>
                  )}
                  <View
                    style={{
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      height: "100%",
                      gap: 5,
                    }}
                  >
                    <View
                      style={{
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 3,
                        height: "100%",
                      }}
                    >
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          color: palette.text.primary,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {breakLongWord(subProduct.productName)}
                      </Text>
                      <Text
                        style={{
                          ...typographyStyle.body2,
                          color: palette.text.primary,
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {breakLongWord(subProduct.name)}
                      </Text>
                    </View>
                  </View>
                </PDFTableCell>
              ))}
            </PDFTableRow>
            {/* Body */}
            <PDFTableBody>
              {data?.map((section, index) => {
                const isSubSectionType =
                  section?.type === FeeAnalysisRowType.SubSection;
                const isFeatureType =
                  section?.type === FeeAnalysisRowType.Breakdown;
                const colorRowDetailMode = isSubSectionType
                  ? "gray"
                  : isFeatureType
                  ? "white"
                  : "dark";

                switch (section.type) {
                  case FeeAnalysisRowType.Section: {
                    return (
                      <PDFReviewTableRow key={index} color={colorRowDetailMode}>
                        <PDFTableCell
                          alignItems="flex-start"
                          width={Configs.FirstCellWidth}
                        >
                          <View
                            style={{
                              gap: DEFAULT_GAP_3,
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            {renderImage(isSubSectionType, section)}
                            <Text
                              style={{
                                ...typographyStyle.body2,
                                color: palette.text.primary,
                                fontWeight:
                                  isSubSectionType || isFeatureType
                                    ? "normal"
                                    : "bold",
                                fontSize: Configs.DefaultFontSize,
                              }}
                            >
                              {section?.name}
                            </Text>
                          </View>
                        </PDFTableCell>
                        {section.analysisData
                          ? section?.analysisData?.map(
                              (
                                analysisData: MAAnalysisDataDTO,
                                index: number,
                              ) => (
                                <PDFTableCell
                                  key={index}
                                  width={Configs.FollowingCellWidth}
                                  hasPadding={false}
                                >
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      color: palette.text.primary,
                                      fontWeight:
                                        isSubSectionType || isFeatureType
                                          ? "normal"
                                          : "bold",
                                      fontSize: Configs.DefaultFontSize,
                                    }}
                                  >
                                    <RenderNumericFeesValue
                                      value={analysisData?.value}
                                      valuePercentage={
                                        analysisData?.valueInPercent
                                      }
                                      valueExcPerf={analysisData?.valueExcPerf}
                                      valuePercentageExcPerf={
                                        analysisData?.valueExcPerfInPercent
                                      }
                                      tableViewMode={tableViewMode}
                                      shouldExcludePerformance={
                                        shouldExcludePerformance
                                      }
                                    />
                                  </Text>
                                </PDFTableCell>
                              ),
                            )
                          : selectedSubProducts?.map(() => (
                              <PDFTableCell
                                key={index}
                                width={Configs.FollowingCellWidth}
                                hasPadding={false}
                              >
                                <Text />
                              </PDFTableCell>
                            ))}
                      </PDFReviewTableRow>
                    );
                  }
                  case FeeAnalysisRowType.SubSection: {
                    return (
                      <PDFReviewTableRow key={index} color={colorRowDetailMode}>
                        <PDFTableCell
                          alignItems="flex-start"
                          width={Configs.FirstCellWidth}
                        >
                          <View
                            style={{
                              gap: DEFAULT_GAP_3,
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            <Text
                              style={{
                                ...typographyStyle.body2,
                                color: palette.text.primary,
                                fontWeight: "bold",
                                fontSize: Configs.DefaultFontSize,
                              }}
                            >
                              {section?.name}
                            </Text>
                          </View>
                        </PDFTableCell>
                        {section.analysisData
                          ? section?.analysisData?.map(
                              (
                                analysisData: MAAnalysisDataDTO,
                                index: number,
                              ) => (
                                <PDFTableCell
                                  key={index}
                                  width={Configs.FollowingCellWidth}
                                  hasPadding={false}
                                >
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      color: palette.text.primary,
                                      fontWeight:
                                        isSubSectionType || isFeatureType
                                          ? "normal"
                                          : "bold",
                                      fontSize: Configs.DefaultFontSize,
                                    }}
                                  >
                                    <RenderNumericFeesValue
                                      value={analysisData?.value}
                                      valuePercentage={
                                        analysisData?.valueInPercent
                                      }
                                      valueExcPerf={analysisData?.valueExcPerf}
                                      valuePercentageExcPerf={
                                        analysisData?.valueExcPerfInPercent
                                      }
                                      tableViewMode={tableViewMode}
                                      shouldExcludePerformance={
                                        shouldExcludePerformance
                                      }
                                    />
                                  </Text>
                                </PDFTableCell>
                              ),
                            )
                          : selectedSubProducts?.map(() => (
                              <PDFTableCell
                                key={index}
                                width={Configs.FollowingCellWidth}
                                hasPadding={false}
                              >
                                <Text
                                  style={{
                                    ...typographyStyle.body2,
                                  }}
                                ></Text>
                              </PDFTableCell>
                            ))}
                      </PDFReviewTableRow>
                    );
                  }
                  case FeeAnalysisRowType.Breakdown:
                    return (
                      <PDFReviewTableRow key={index} color={colorRowDetailMode}>
                        <PDFTableCell
                          alignItems="flex-start"
                          width={Configs.FirstCellWidth}
                        >
                          <View
                            style={{
                              gap: DEFAULT_GAP_3,
                              flexDirection: "row",
                              alignItems: "center",
                            }}
                          >
                            {renderImage(isSubSectionType, section)}
                            <Text
                              style={{
                                ...typographyStyle.body2,
                                color: palette.text.primary,
                                fontWeight:
                                  isSubSectionType || isFeatureType
                                    ? "normal"
                                    : "bold",
                                fontSize: Configs.DefaultFontSize,
                              }}
                            >
                              {section?.name}
                            </Text>
                          </View>
                        </PDFTableCell>
                        {section?.analysisData?.map(
                          (analysisData: MAAnalysisDataDTO, index: number) => (
                            <PDFTableCell
                              key={index}
                              width={Configs.FollowingCellWidth}
                              hasPadding={false}
                            >
                              {renderAnalysisDataValue(
                                section.variableName ?? "",
                                analysisData,
                                analysisData?.fieldTypeId,
                              )}
                            </PDFTableCell>
                          ),
                        )}
                      </PDFReviewTableRow>
                    );
                  default:
                    return <></>;
                }
              })}
            </PDFTableBody>
          </PDFTable>

          {shouldShowRedStarInformation && (
            <View style={{ marginTop: 5 }}>
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.error.main,
                }}
              >
                * See web version of this report for full details
              </Text>
            </View>
          )}
        </>
      )}

      {!isNil(numOfHiddenProducts) && (
        <View style={{ marginTop: 10 }}>
          <Text
            style={{
              ...typographyStyle.body2,
              color: "#1E1970",
              fontWeight: "bold",
            }}
          >
            {`${numOfHiddenProducts} ${
              numOfHiddenProducts === 1 ? "product" : "products"
            } hidden`}
          </Text>
        </View>
      )}
    </>
  );
};
